<template>
  <div class="display-settings">
    <div class="settings-card">
      <el-form label-position="top" class="display-form">

      <el-form-item :label="$t('settings.language')">
        <el-select 
          v-model="language" 
          @change="handleLanguageChange" 
          class="language-select"
        >
          <el-option value="zh" label="中文" />
          <el-option value="en" label="English" />
          <el-option value="ug" label="ئۇيغۇرچە" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('settings.fontSize')">
        <el-radio-group 
          v-model="fontSize" 
          @change="handleFontSizeChange" 
          class="responsive-radio-group"
        >
          <el-radio-button value="smaller">{{ $t('settings.smaller') }}</el-radio-button>
          <el-radio-button value="small">{{ $t('settings.small') }}</el-radio-button>
          <el-radio-button value="medium">{{ $t('settings.medium') }}</el-radio-button>
          <el-radio-button value="large">{{ $t('settings.large') }}</el-radio-button>
          <el-radio-button value="xlarge">{{ $t('settings.xlarge') }}</el-radio-button>
          <el-radio-button value="bigger">{{ $t('settings.bigger') }}</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item :label="$t('settings.fontFamily')">
        <el-select v-model="fontFamily" @change="handleFontFamilyChange" class="font-family-select">
          <el-option value="system" label="System Default" />
          <el-option value="ukij" label="UKIJ Ekran" />
          <el-option value="pingfang" label="PingFang SC" />
          <el-option value="microsoft" label="Microsoft YaHei" />
        </el-select>
      </el-form-item>

      <!-- Font Size Preview -->
      <el-form-item :label="$t('settings.fontSizePreview')">
        <div class="font-preview">
          <div class="preview-section">
            <div class="preview-title">{{ $t('settings.previewTitle') }}</div>
            <div class="preview-subtitle">{{ $t('settings.previewSubtitle') }}</div>
            <div class="preview-body">{{ $t('settings.previewBody') }}</div>
            <div class="preview-small">{{ $t('settings.previewSmall') }}</div>
          </div>
        </div>
      </el-form-item>

      <!-- Chrome/Edge Installation Instructions -->
      <el-divider />
      <el-form-item :label="$t('settings.chromeEdgeInstall')">
        <div class="chrome-edge-instructions">
          <el-alert
            type="success"
            :closable="false"
            show-icon
            class="chrome-edge-install-tip"
          >
            <template #title>
              <strong>{{ $t('settings.chromeEdgeInstallTitle') }}</strong>
            </template>
            <template #default>
              <p>{{ $t('settings.chromeEdgeInstruction') }}</p>
            </template>
          </el-alert>
        </div>
      </el-form-item>

      <!-- Safari Installation Instructions -->
      <el-divider />
      <el-form-item :label="$t('settings.safariInstall')">
        <div class="safari-instructions">
          <el-alert
            type="info"
            :closable="false"
            show-icon
            class="safari-install-tip"
          >
            <template #title>
              <strong>{{ $t('settings.safariInstallTitle') }}</strong>
            </template>
            <template #default>
              <p>{{ $t('settings.safariMacInstruction') }}</p>
              <p>{{ $t('settings.safariIOSInstruction') }}</p>
            </template>
          </el-alert>
        </div>
      </el-form-item>
    </el-form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAppStore } from '@/stores/app'

const appStore = useAppStore()

const fontSize = ref('medium')
const fontFamily = ref('system')
const language = ref('zh')
const theme = ref('light')

const handleFontSizeChange = (value) => {
  appStore.setFontSize(value)
}

const handleFontFamilyChange = (value) => {
  appStore.setFontFamily(value)
}

const handleLanguageChange = (value) => {
  appStore.setLanguage(value)
  window.location.reload()
}

onMounted(() => {
  fontSize.value = appStore.fontSize
  fontFamily.value = appStore.fontFamily
  language.value = appStore.language
  theme.value = appStore.theme
})
</script>

<style scoped lang="scss">
.display-settings {
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 4px;
  }
  
  @media (max-width: 480px) {
    padding: 0 2px;
  }
  
  .settings-card {
    background: var(--bg-color-white, #ffffff);
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: background-color 0.3s ease;
    
    @media (max-width: 768px) {
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      border-radius: 8px;
    }
    
    html.dark & {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
    }
  }

  .display-form {
    padding: 24px;
    
    @media (max-width: 768px) {
      padding: 20px;
    }
    
    @media (max-width: 480px) {
      padding: 16px;
    }
  }

  :deep(.el-form-item) {
    margin-bottom: 28px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    @media (max-width: 768px) {
      margin-bottom: 24px;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 20px;
    }
    
    :deep(.el-form-item__label) {
      font-weight: 600;
      font-size: var(--font-size-base);
      color: var(--color-text-primary, #303133);
      margin-bottom: 10px;
      padding-bottom: 0;
      line-height: 1.5;
      
      @media (max-width: 768px) {
        font-size: var(--font-size-sm);
        margin-bottom: 8px;
      }
      
      html.dark & {
        color: var(--color-text-regular, #e2e8f0);
      }
    }
  }

  .language-select {
    width: 100%;
    max-width: 300px;
    
    @media (max-width: 768px) {
      max-width: 100%;
    }
  }
  
  .font-family-select {
    width: 100%;
    max-width: 300px;
    
    @media (max-width: 768px) {
      max-width: 100%;
    }
  }

  .responsive-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    
    @media (max-width: 768px) {
      gap: 6px;
    }
    
    @media (max-width: 480px) {
      gap: 5px;
    }
    
    :deep(.el-radio-button) {
      flex: 1;
      min-width: 0;
      
      @media (max-width: 768px) {
        flex: 1 1 calc(50% - 4px);
      }
      
      @media (max-width: 360px) {
        flex: 1 1 100%;
      }
    }
    
    :deep(.el-radio-button__inner) {
      border-radius: 8px !important;
      padding: 10px 16px;
      font-size: var(--font-size-base);
      font-weight: 500;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: var(--font-size-sm);
        min-height: 36px;
      }
    }
  }

  .font-preview {
    padding: 24px;
    background: var(--bg-color-light, #f5f7fa);
    border-radius: 12px;
    border: 1px solid var(--border-color-light, #e4e7ed);
    transition: background-color 0.3s ease, border-color 0.3s ease;
    overflow-x: hidden;
    
    @media (max-width: 768px) {
      padding: 20px;
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      padding: 16px;
      border-radius: 8px;
    }
    
    html.dark & {
      background: var(--bg-color-base, #2d3748);
      border-color: var(--border-color-light, #4a5568);
    }

    .preview-section {
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      @media (max-width: 768px) {
        gap: 14px;
      }
      
      @media (max-width: 480px) {
        gap: 12px;
      }

      .preview-title {
        font-size: var(--font-size-2xl);
        font-weight: 700;
        color: var(--color-text-primary, #303133);
        line-height: 1.3;
        letter-spacing: -0.02em;
        
        html.dark & {
          color: var(--color-text-primary, #f7fafc);
        }
      }

      .preview-subtitle {
        font-size: var(--font-size-lg);
        font-weight: 500;
        color: var(--color-text-regular, #606266);
        line-height: 1.4;
        
        html.dark & {
          color: var(--color-text-secondary, #cbd5e0);
        }
      }

      .preview-body {
        font-size: var(--font-size-base);
        color: var(--color-text-primary, #303133);
        line-height: 1.6;
        
        html.dark & {
          color: var(--color-text-regular, #e2e8f0);
        }
      }

      .preview-small {
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary, #909399);
        line-height: 1.5;
        font-style: italic;
        
        html.dark & {
          color: var(--color-text-placeholder, #a0aec0);
        }
      }
    }
  }

  :deep(.el-divider) {
    html.dark & {
      border-color: var(--border-color-light, #4a5568);
    }
  }

  .chrome-edge-instructions {
    .chrome-edge-install-tip {
      html.dark & {
        background-color: var(--bg-color-base, #2d3748);
        border-color: var(--border-color-light, #4a5568);
      }
      
      :deep(.el-alert__title) {
        font-size: var(--font-size-base);
        margin-bottom: 8px;
      }

      :deep(.el-alert__content) {
        font-size: var(--font-size-sm);
        line-height: 1.6;
        
        p {
          margin: 8px 0;
          
          &:first-child {
            margin-top: 0;
          }
          
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }

  .safari-instructions {
    .safari-install-tip {
      html.dark & {
        background-color: var(--bg-color-base, #2d3748);
        border-color: var(--border-color-light, #4a5568);
      }
      
      :deep(.el-alert__title) {
        font-size: var(--font-size-base);
        margin-bottom: 8px;
      }

      :deep(.el-alert__content) {
        font-size: var(--font-size-sm);
        line-height: 1.6;
        
        p {
          margin: 8px 0;
          
          &:first-child {
            margin-top: 0;
          }
          
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}
</style>
